import { useFeatureFlag } from '@agentnet/feature-flags';
import { FeatureFlag } from 'utilities/constants/launch-darkly-flags';
import { fileStatuses } from '../constants';

/**
 * Determines if a file allows service order amendments based on status and feature flag.
 *
 * Business Logic:
 * - ALWAYS allow amendments on Open files (backward compatibility)
 * - CONDITIONALLY allow amendments on Closed/Open Remitted files if feature flag enabled
 *
 * @param fileStatus - Current file status ('Open', 'Closed', 'Open Remitted', etc.)
 * @param flagEnabled - Feature flag state from useFeatureFlag()
 * @returns true if file can be amended
 */
export const isFileAmendable = (fileStatus: string | undefined, flagEnabled: boolean): boolean => {
  if (!fileStatus) return false;

  // Always allow Open files (existing behavior)
  if (fileStatus === fileStatuses.open) return true;

  // If feature flag is disabled, no other statuses are amendable
  if (!flagEnabled) return false;

  // Flag is enabled: allow Closed and Open Remitted files
  return fileStatus === fileStatuses.closed || fileStatus === fileStatuses.openRemitted;
};

/**
 * Automatically fetches feature flag state and applies file amendability logic.
 *
 * @param fileStatus - Current file status
 * @returns true if file can be amended
 */
export const useFileAmendability = (fileStatus: string | undefined): boolean => {
  const flagState = useFeatureFlag(FeatureFlag.SERVICE_ORDER_ANS_7540_AMEND_CLOSED_FILES, false);
  return isFileAmendable(fileStatus, flagState?.value ?? false);
};
